/**
 * useBrandAsset — resolves a public brand asset by tag from the
 * Brand Asset Library (single source of truth). Uploading a new asset
 * with the matching tag instantly propagates everywhere.
 *
 * Usage:
 *   const wordmark = useBrandAsset("wordmark.primary");
 *   const x        = useBrandAsset("mascot.brand-ambassador", { fallback: "mascot.rocket" });
 *
 * Pass `fallbackUrl` for a hard fallback if the registry is empty.
 */
import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";

const cache = new Map();

async function fetchAsset(tag) {
  if (cache.has(tag)) return cache.get(tag);
  try {
    const { data } = await api.get(`/brand-assets/key/${encodeURIComponent(tag)}`);
    cache.set(tag, data);
    return data;
  } catch (_) {  // eslint-disable-line no-unused-vars
    cache.set(tag, null);
    return null;
  }
}

export default function useBrandAsset(tag, { fallback, fallbackUrl } = {}) {
  const [asset, setAsset] = useState(() => cache.get(tag));
  const [fallbackAsset, setFallbackAsset] = useState(() => fallback ? cache.get(fallback) : null);

  useEffect(() => {
    let active = true;
    (async () => {
      const a = await fetchAsset(tag);
      if (active) setAsset(a);
      if (!a && fallback) {
        const f = await fetchAsset(fallback);
        if (active) setFallbackAsset(f);
      }
    })();
    return () => { active = false; };
  }, [tag, fallback]);

  return useMemo(() => {
    const a = asset || fallbackAsset;
    return {
      url: a?.url || fallbackUrl,
      label: a?.label,
      meta: a,
      ready: asset !== undefined || fallbackAsset !== undefined,
    };
  }, [asset, fallbackAsset, fallbackUrl]);
}
